<template>
  <div class="login">
    <img class="login__bg" src="@/assets/login_background.jpg" alt="" aria-hidden="true" />
    <div class="login__veil" aria-hidden="true"></div>

    <main class="shell">
      <section class="glass" aria-labelledby="loginTitle">
        <header class="glass__header">
          <img class="glass__logo" src="@/assets/logogruas.png" alt="Grúas Monterrey" />
          <h1 id="loginTitle">Bienvenido</h1>
          <p class="glass__sub">Inicia sesión para acceder al sistema</p>
        </header>

        <form @submit.prevent="submitLogin" novalidate>
          <div class="field">
            <label for="user" class="field__label">Usuario</label>
            <div class="field__control">
              <span class="field__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm7 9a7 7 0 0 0-14 0" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
              <input
                id="user"
                v-model.trim="username"
                type="text"
                class="field__input"
                required
                autocomplete="username"
                :disabled="loading"
                placeholder="usuario"
              />
            </div>
          </div>

          <div class="field">
            <label for="pass" class="field__label">Contraseña</label>
            <div class="field__control">
              <span class="field__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M6 10V8a6 6 0 1 1 12 0v2M6 10h12v10H6z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
              <input
                id="pass"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="field__input field__input--trailing"
                required
                autocomplete="current-password"
                :disabled="loading"
                placeholder="••••••••"
              />
              <button
                type="button"
                class="field__toggle"
                @click="togglePasswordVisibility"
                :aria-pressed="showPassword ? 'true' : 'false'"
                :disabled="loading"
              >
                <svg v-if="!showPassword" viewBox="0 0 24 24" aria-hidden="true"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>
                <svg v-else viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3l18 18M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2M9.88 4.12A12.34 12.34 0 0 1 12 4c7 0 11 8 11 8a18.94 18.94 0 0 1-4.13 5.36M6.7 6.7C3.93 8.49 2 12 2 12a19.05 19.05 0 0 0 5.16 6.11" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
                <span class="sr-only">{{ showPassword ? 'Ocultar' : 'Mostrar' }} contraseña</span>
              </button>
            </div>
          </div>

          <label class="remember">
            <input type="checkbox" v-model="remember" :disabled="loading" />
            <span class="remember__box" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
            <span>Mantener sesión iniciada</span>
          </label>

          <button
            class="submit"
            type="submit"
            :disabled="loading || !username || !password"
            :aria-busy="loading ? 'true' : 'false'"
          >
            <span v-if="loading" class="submit__spinner" aria-hidden="true"></span>
            <span>{{ loading ? 'Ingresando…' : 'Iniciar sesión' }}</span>
          </button>

          <transition name="fade">
            <div v-if="error" class="alert" role="alert" aria-live="polite">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span>{{ errorMessage }}</span>
            </div>
          </transition>
        </form>

        <footer class="glass__footer">
          Sistema Grúas Monterrey · {{ new Date().getFullYear() }}
        </footer>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "LoginModern",
  data() {
    return {
      username: "",
      password: "",
      remember: true,
      showPassword: false,
      loading: false,
      error: false,
      errorMessage: "",
    };
  },
  mounted() {
    // autofocus username for quicker entry
    this.$nextTick(() => {
      const el = this.$el.querySelector("#user");
      if (el) el.focus();
    });
  },
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    safeNextPath() {
      // Only honor same-origin relative paths, so a crafted link cannot use
      // the login page as an open redirect ("//evil.com", "https://...",
      // backslash tricks). Anything suspicious falls back to normal routing.
      const next = this.$route.query.next;
      if (typeof next !== "string" || !next.startsWith("/")) return null;
      if (next.startsWith("//") || next.includes("\\")) return null;
      return next;
    },
    async submitLogin() {
      if (!this.username || !this.password) return;
      this.loading = true;
      this.error = false;
      this.errorMessage = "";

      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const { data } = await axios.post(`${apiDirectory}/login/`, {
          username: this.username,
          password: this.password,
        });

        // persist token
        localStorage.setItem("authToken", data.token);
        if (this.remember) {
          localStorage.setItem("authUser", this.username);
        } else {
          localStorage.removeItem("authUser");
        }

        this.$store.commit("setToken", data.token);

        try {
          const currentUser = await axios.get(`${apiDirectory}/current-user/`, {
            headers: { Authorization: `Token ${data.token}` },
          });
          const permissions = Array.isArray(currentUser.data?.permissions)
            ? currentUser.data.permissions
            : [];
          localStorage.setItem("userPermissions", JSON.stringify(permissions));
        } catch (err) {
          localStorage.setItem("userPermissions", JSON.stringify([]));
        }

        // OAuth/session flows (e.g. the MCP assistant consent screen) land
        // here with ?next=<path>. The login POST above also established a
        // Django session cookie, so a full-page navigation to that path is
        // now authenticated. Validated to same-origin paths only.
        const nextPath = this.safeNextPath();
        if (nextPath) {
          window.location.assign(nextPath);
          return;
        }

        // route by role
        if (data.is_operator) {
          this.$router.push({ name: "OperatorInterface" });
        } else {
          this.$router.push({ name: "MainPanel" });
        }
      } catch (err) {
        console.error(err);
        this.error = true;
        const responseData = err.response && err.response.data;
        this.errorMessage =
          (responseData && (responseData.error || responseData.detail)) ||
          "Credenciales incorrectas.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login {
  --brand: #ffc434;
  --brand-deep: #f0ac00;
  --ink: #1d1812;
  --ink-soft: #4c453c;
  --ink-faint: #7a7267;
  --glass-line: rgba(255, 255, 255, 0.65);
  --focus-ring: rgba(255, 196, 52, 0.5);

  position: fixed;
  inset: 0;
  overflow: hidden;
  font-family: "Poppins", system-ui, sans-serif;
  background: #14100a;
  /* #app sets text-align: center globally; reset it here */
  text-align: left;
}

/* ---------- background ---------- */
.login__bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 68%;
  transform: scale(1.05);
  animation: slowZoom 26s ease-out both;
}
.login__veil {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(120% 90% at 50% 30%, rgba(10, 10, 12, 0) 40%, rgba(10, 10, 12, 0.5) 100%),
    linear-gradient(180deg, rgba(10, 10, 12, 0.25), rgba(10, 10, 12, 0.12) 45%, rgba(10, 10, 12, 0.4));
}

/* ---------- layout ---------- */
.shell {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}

/* ---------- glass card ---------- */
.glass {
  width: min(100%, 400px);
  padding: 36px 32px 26px;
  border-radius: 26px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.6));
  backdrop-filter: blur(22px) saturate(150%);
  -webkit-backdrop-filter: blur(22px) saturate(150%);
  border: 1px solid var(--glass-line);
  box-shadow:
    0 30px 70px rgba(8, 6, 2, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
  animation: rise 0.6s ease both;
}

.glass__header {
  text-align: center;
  margin-bottom: 26px;
}
.glass__logo {
  height: 46px;
  width: auto;
  margin-bottom: 20px;
}
.glass__header h1 {
  margin: 0 0 4px;
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
}
.glass__sub {
  margin: 0;
  font-size: 0.9rem;
  color: var(--ink-soft);
}

/* ---------- fields ---------- */
.field {
  margin-bottom: 16px;
}
.field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ink-soft);
}
.field__control {
  position: relative;
}
.field__icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: var(--ink-faint);
  pointer-events: none;
  transition: color 160ms ease;
}
.field__icon svg {
  width: 18px;
  height: 18px;
  display: block;
}
.field__control:focus-within .field__icon {
  color: var(--brand-deep);
}
.field__input {
  width: 100%;
  padding: 12px 14px 12px 43px;
  border-radius: 999px;
  border: 1px solid rgba(29, 24, 18, 0.14);
  background: rgba(255, 255, 255, 0.55);
  color: var(--ink);
  font-family: inherit;
  font-size: 0.93rem;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}
.field__input--trailing {
  padding-right: 46px;
}
.field__input::placeholder {
  color: var(--ink-faint);
  font-weight: 300;
}
.field__input:hover:not(:disabled) {
  border-color: rgba(29, 24, 18, 0.28);
}
.field__input:focus {
  border-color: var(--brand-deep);
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 0 4px var(--focus-ring);
}
.field__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.field__toggle {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-faint);
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease;
}
.field__toggle:hover:not(:disabled) {
  background: rgba(29, 24, 18, 0.08);
  color: var(--ink);
}
.field__toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.field__toggle svg {
  width: 18px;
  height: 18px;
}

/* ---------- remember ---------- */
.remember {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 4px 0 20px;
  font-size: 0.85rem;
  color: var(--ink-soft);
  cursor: pointer;
  user-select: none;
}
.remember input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}
.remember__box {
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid rgba(29, 24, 18, 0.3);
  background: rgba(255, 255, 255, 0.6);
  color: var(--ink);
  transition: background 140ms ease, border-color 140ms ease;
}
.remember__box svg {
  width: 11px;
  height: 11px;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 140ms ease, transform 140ms ease;
}
.remember input:checked + .remember__box {
  background: var(--brand);
  border-color: var(--brand);
}
.remember input:checked + .remember__box svg {
  opacity: 1;
  transform: scale(1);
}
.remember input:focus-visible + .remember__box {
  box-shadow: 0 0 0 4px var(--focus-ring);
}

/* ---------- submit ---------- */
.submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  padding: 13px 18px;
  border: 0;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.96rem;
  font-weight: 600;
  color: #241a04;
  background: linear-gradient(135deg, #ffd257, var(--brand) 55%, #f5b71e);
  box-shadow: 0 12px 26px rgba(240, 172, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.45);
  cursor: pointer;
  transition: transform 140ms ease, box-shadow 160ms ease, filter 160ms ease, opacity 120ms ease;
}
.submit:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.04);
  box-shadow: 0 16px 32px rgba(240, 172, 0, 0.46), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}
.submit:active:not(:disabled) {
  transform: translateY(0);
}
.submit:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px var(--focus-ring), 0 12px 26px rgba(240, 172, 0, 0.4);
}
.submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}
.submit__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(36, 26, 4, 0.3);
  border-top-color: #241a04;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ---------- alert ---------- */
.alert {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 14px;
  padding: 11px 14px;
  border-radius: 14px;
  font-size: 0.85rem;
  color: #7e1a1a;
  background: rgba(255, 235, 235, 0.85);
  border: 1px solid rgba(224, 43, 43, 0.35);
}
.alert svg {
  flex: 0 0 17px;
  width: 17px;
  height: 17px;
  margin-top: 1px;
  color: #d33030;
}

.glass__footer {
  margin-top: 24px;
  font-size: 0.75rem;
  color: var(--ink-faint);
  text-align: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 1px, 1px);
  white-space: nowrap;
  border: 0;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes slowZoom {
  from {
    transform: scale(1.12);
  }
  to {
    transform: scale(1.05);
  }
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ---------- responsive ---------- */
@media (max-width: 480px) {
  .shell {
    padding: 16px;
  }
  .glass {
    padding: 30px 22px 22px;
  }
  .glass__logo {
    height: 40px;
    margin-bottom: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login__bg,
  .glass {
    animation: none;
  }
  .field__input,
  .field__toggle,
  .submit,
  .remember__box,
  .remember__box svg {
    transition: none;
  }
}
</style>
